<script>
  import { ShieldCheck, ShieldAlert, ShieldQuestion } from '../lib/icons/index.js';
  import InfoHint from './InfoHint.svelte';
  import { t } from '../i18n/index.js';
  import { formatDate, formatDateTime } from '../lib/labels.js';
  import { signatureView, signerLabelKey, referenceSourceKey } from '../lib/signature-verification.js';

  // GET /invoices/:id/signature for a received invoice: the advisory check of
  // the supplier's signature (#44) and the signing time the envelope declares
  // (#43). Shown only when there is a signature to talk about.
  export let signature = null;
  // Whether the invoice was imported rather than delivered by SdI: only SdI
  // checks the signature before handing a document over. Taken from the
  // invoice, not from the verdict, which says nothing while it is pending or
  // when no reference time was needed.
  export let imported = false;

  $: verification = signature?.verification ?? null;
  $: view = signatureView(verification);
  $: signer = verification?.signer ?? null;
  $: checked = !!verification?.verifiedAt;
  // The note about today's AgID list only explains a verdict that was reached
  // against it: not an altered document, nor one that could not be judged.
  $: chainJudged = ['ok', 'untrusted', 'expired', 'not_yet_valid'].includes(verification?.reason);
  $: sourceKey = referenceSourceKey(verification?.referenceTimeSource);
  $: icon = view?.badge === 'badge-green' ? ShieldCheck
    : (view?.badge === 'badge-red' || view?.badge === 'badge-yellow') ? ShieldAlert
      : ShieldQuestion;
</script>

{#if view}
  <section class="mb-8" aria-labelledby="signature-check-title">
    <div class="section-heading">
      <h2 id="signature-check-title" class="inline-flex items-center gap-2">
        <svelte:component this={icon} size={18} aria-hidden="true" />
        {$t('invoices.signatureCheck.title')}<InfoHint term="verificaFirma" />
      </h2>
    </div>
    <p class="text-xs text-muted mb-4 measure">
      {$t('invoices.signatureCheck.intro')}
      {#if verification.format === 'xades'}{$t('invoices.signatureCheck.introXades')}{/if}
      {#if imported}{$t('invoices.signatureCheck.introImported')}{:else}{$t('invoices.signatureCheck.introSdi')}{/if}
    </p>

    <p class="mb-2">
      <span class="badge whitespace-normal {view.badge}">{$t(view.labelKey)}</span>
    </p>
    <p class="text-sm measure mb-4">{$t(view.explanationKey)}</p>

    <dl class="detail-meta">
      {#if signer}
        <div>
          <dt>{$t(signerLabelKey(verification))}</dt>
          <dd>{signer.commonName || '-'}</dd>
        </div>
        {#if signer.fiscalCode}
          <div>
            <dt>{$t('invoices.signatureCheck.fields.signerFiscalCode')}</dt>
            <dd class="mono">{signer.fiscalCode}</dd>
          </div>
        {/if}
        <div>
          <dt class="inline-flex items-center gap-1">{$t('invoices.signatureCheck.fields.issuer')}<InfoHint term="elencoFiducia" /></dt>
          <dd>{signer.issuerCommonName || '-'}</dd>
        </div>
        {#if signer.validFrom && signer.validTo}
          <div>
            <dt>{$t('invoices.signatureCheck.fields.certValidity')}</dt>
            <dd>{$t('invoices.signatureCheck.fields.certValidityValue', { from: formatDate(signer.validFrom), to: formatDate(signer.validTo) })}</dd>
          </div>
        {/if}
      {/if}
      {#if checked && verification.format !== 'xades'}
        <div>
          <dt class="inline-flex items-center gap-1">{$t('invoices.signatureCheck.fields.signingTime')}<InfoHint text={$t('invoices.signatureCheck.fields.signingTimeHint')} /></dt>
          <dd>{signature.signingTime ? formatDateTime(signature.signingTime) : $t('invoices.signatureCheck.fields.signingTimeAbsent')}</dd>
        </div>
      {/if}
      {#if signature?.signedAt}
        <div>
          <dt>{$t('invoices.signatureCheck.fields.receivedAt')}</dt>
          <dd>{formatDateTime(signature.signedAt)}</dd>
        </div>
      {/if}
      {#if checked}
        <div>
          <dt>{$t('invoices.signatureCheck.fields.verifiedAt')}</dt>
          <dd>{formatDateTime(verification.verifiedAt)}</dd>
        </div>
      {/if}
      {#if verification?.referenceTime && sourceKey}
        <div>
          <dt>{$t('invoices.signatureCheck.fields.referenceTime')}</dt>
          <dd>{$t('invoices.signatureCheck.fields.referenceTimeValue', { time: formatDateTime(verification.referenceTime), source: $t(sourceKey) })}</dd>
        </div>
      {/if}
    </dl>

    {#if checked}
      <ul class="text-xs text-muted mt-4 measure signature-notes">
        {#if verification.signerInfoCount > 1}<li>{$t('invoices.signatureCheck.notes.multiSigner')}</li>{/if}
        {#if verification.trustList?.stale}<li>{$t('invoices.signatureCheck.notes.staleList')}</li>{/if}
        {#if chainJudged}<li>{$t('invoices.signatureCheck.notes.trustList')}</li>{/if}
        <li>{$t('invoices.signatureCheck.notes.revocation')}</li>
      </ul>
    {/if}
  </section>
{/if}

<style>
  .signature-notes {
    list-style: disc;
    padding-left: 1.25rem;
  }
  .signature-notes li + li {
    margin-top: 0.25rem;
  }
</style>
